import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageLoadTracking } from '@/hooks/pageLoadTracking/usePageLoadTracking'
import { useIsOnCustomSize } from '@/hooks/screen/useIsOnCustomSize'

import { debounce } from '@/utils/functionalUtils'

import { ButtonSpinner, PrimaryButton } from '@/components/buttons/primaryButton'
import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { TextBlock } from '@/components/textBlock'
import { TextHTML } from '@/components/typography'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageComplete } from '@/events/events'

import { TInfoPageProps } from './types'

export const InfoPage = ({
  pageName,
  parameter,
  pageConfig,
  setLastVisitedItem,
  config,
}: TInfoPageProps) => {
  const { user } = useAuth()
  const sessionContext = useSession()
  const [isLoading, setIsLoading] = useState(false)

  const {
    title,
    nextUrl,
    pageDescription,
    tabTitle,
    replaceParameter,
    infoImage,
    infoImageDesktop,
    textBlocks,
    buttonName,
    buttonFloating,
    childComponent,
    hideSubmitButton = false,
    policyBlock = false,
    gifBlock,
    childInfoImageDesktop,
  } = pageConfig

  const navigate = useNavigate()
  const isDesktop = useIsOnCustomSize(1280, 'min-width')

  const description =
    parameter && pageDescription && replaceParameter
      ? replaceParameter(parameter, pageDescription)
      : pageDescription

  const pageTitle = typeof title === 'function' ? title() : title
  usePageLoadTracking({
    pageName,
    config,
    user,
    title: tabTitle || pageTitle,
  })

  const nextPage = async () => {
    setIsLoading(true)
    trackPageComplete(
      pageName,
      [],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )

    await debounce(() => {
      navigate(nextUrl)
      setLastVisitedItem(pageName)
    }, 300)()
  }

  const className = `page page-survey info-page page-${pageName}`
  return (
    <div className={className}>
      <div className="content-wrapper">
        {gifBlock && (
          <div className="gif-container">
            <img src={gifBlock} alt="Progress" className="gif" />
          </div>
        )}
        <PageTitle title={pageTitle} />

        {description && <TextHTML className="page-description" title={description} />}

        {infoImage && !isDesktop && (
          <div className="info-image-container">
            <img src={infoImage.src} alt="" />
            {childInfoImageDesktop && (
              <div className="child-container">{childInfoImageDesktop}</div>
            )}
          </div>
        )}

        {infoImageDesktop && isDesktop && (
          <div className="info-image-container">
            <img src={infoImageDesktop.src} alt="" />
          </div>
        )}

        {childComponent && (
          <div className="info-component-container">
            {typeof childComponent === 'function' ? childComponent({ nextPage }) : childComponent}
          </div>
        )}

        {textBlocks &&
          textBlocks.map(({ text, ...blockProps }) => {
            text = typeof text === 'function' ? text() : text
            return <TextBlock key={text} text={text} {...blockProps} />
          })}
      </div>

      {!hideSubmitButton && (
        <div className={`button-container ${buttonFloating ? 'float' : ''}`}>
          <PrimaryButton disabled={isLoading} onClick={nextPage}>
            {isLoading ? <ButtonSpinner /> : buttonName || 'Continue'}
          </PrimaryButton>
        </div>
      )}

      {policyBlock && <FooterPolicy />}
    </div>
  )
}
